import { addDays, format, parseISO } from 'date-fns';

import agent, { prepareHeaders } from 'api/agent';
import {
    DATE_FORMAT,
    findTakenTable,
    toWireDateTime,
    WIRE_DATE_TIME_FORMAT,
} from 'helpers/reservationHelper';

export type ReservationStatus =
    | 'Requested'
    | 'Confirmed'
    | 'Seated'
    | 'Declined'
    | 'Archived'
    | 'Cancelled'
    | 'Completed'
    | 'NoShow';

export type ReservationChannel = 'WebApp' | 'Staff' | 'Phone' | 'Email' | 'WalkIn';
export type ReservationStatusTransition = 'Declined' | 'Seated' | 'Cancelled' | 'NoShow' | 'Completed';
export type ReservationCancellationReason = 'GuestCancelled' | 'RestaurantClosed' | 'Other';

export interface CancelReservationModel {
    expectedVersion: number;
    cancellationReason: ReservationCancellationReason;
}

export interface ChangeReservationStatusModel {
    status: ReservationStatusTransition;
    expectedVersion?: number;
    cancellationReason?: ReservationCancellationReason;
}

export interface DeclineRequestModel {
    expectedVersion: number;
    reason: string | null;
}

export type RequestClosureResult =
    | {
          status: 'declined' | 'cancelled';
          reservation: ReservationDTO;
          cancellationEmailFailed?: boolean;
      }
    | { status: 'alreadyHandled'; refreshFailed?: boolean };

export type CancelReservationResult =
    | { status: 'cancelled'; reservation: ReservationDTO; cancellationEmailFailed: boolean }
    | { status: 'ordersExist'; tables: ReservationCancellationTableDTO[] }
    | { status: 'alreadyHandled' };

export interface ReservationCancellationTableDTO {
    tableId: string;
    tableName: string;
}

export interface ReservationCancellationAvailabilityDTO {
    canCancel: boolean;
    tables: ReservationCancellationTableDTO[];
    blockingTables: ReservationCancellationTableDTO[];
}

export interface ReservationDTO {
    id: string;
    restaurantId: string;
    status: ReservationStatus;
    isArchived: boolean;
    channel: ReservationChannel;
    internalNote?: string | null;
    version?: number;
    reservationStartDateTime: string;
    reservationEndDateTime: string;
    reservationDurationInMinutes: number;
    guestsNumber: number;
    isGuestsNumberApproximate: boolean;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    allergies: string[];
    otherAllergies: string | null;
    clientNote: string | null;
    tables: string[];
    createDate: string;
    confirmedAt: string | null;
    seatedAt?: string | null;
    completedAt?: string | null;
    completedByUserId?: string | null;
    declineReason?: string | null;
    declinedAt?: string | null;
    declinedByUserId?: string | null;
    cancellationReason?: ReservationCancellationReason | null;
    cancelledAt?: string | null;
    cancelledByUserId?: string | null;
}

export interface ReservationTableOrderStateDTO {
    tableId: string;
    tableName: string;
    hasActiveOrder: boolean;
}

export interface UpdateReservationModel {
    expectedVersion?: number;
    channel?: ReservationChannel | null;
    internalNote?: string;
    reservationStartDateTime: string;
    reservationDurationInMinutes: number;
    guestsNumber: number;
    isGuestsNumberApproximate: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    email: string | null;
    allergies: string[];
    otherAllergies: string;
    clientNote: string;
    tables: string[];
}

interface ReservationResultDTO {
    reservation: ReservationDTO;
    isOutsideOpeningHours: boolean;
    hasDuplicateGuest: boolean;
    updatedEmailSent?: boolean;
    updatedEmailFailed?: boolean;
}

export interface TableAvailabilityDTO {
    tableId: string;
    isBooked: boolean;
    bookedFrom: string | null;
    hasActiveOrder: boolean;
}

export type SaveRequestResult =
    | { status: 'saved'; reservation: ReservationDTO }
    | { status: 'alreadyHandled' };

export type ConfirmRequestResult =
    | { status: 'confirmed'; reservation: ReservationDTO }
    | { status: 'tableTaken'; tableId: string }
    | { status: 'alreadyHandled' };

export type UpdateConfirmedResult =
    | {
          status: 'updated';
          reservation: ReservationDTO;
          updatedEmailSent?: boolean;
          updatedEmailFailed?: boolean;
      }
    | { status: 'tableTaken'; tableId: string }
    | { status: 'alreadyHandled' };

export type SeatedEditConflict =
    | { status: 'removedTableOrderOpen'; tableId: string }
    | { status: 'retainedTableTaken'; tableId: string; message: string };

export type UpdateSeatedResult = UpdateConfirmedResult | SeatedEditConflict;

export type ArchiveRequestResult = 'archived' | 'alreadyHandled';

/** Search is already normalized: empty, or at least 2 characters (see normalizeReservationSearch). */
export interface ReservationListFilter {
    search: string;
    day: string | null;
}

export interface ReservationDaySummaryDTO {
    reservations: number;
    requests: number;
    seatsBooked: number;
    seatsTotal: number;
    tablesBooked: number;
    tablesTotal: number;
}

export type CreateReservationResult =
    | { status: 'created'; reservation: ReservationDTO }
    | { status: 'tableTaken'; tableId: string };

const BASE = '/Reservation';

export interface ConflictBody {
    StatusCode: number;
    Message: string;
    Code?: string;
    TableId?: string;
    Tables?: ReservationCancellationTableDTO[];
}

export function isConflict(error: unknown): error is ConflictBody {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as Partial<ConflictBody>).StatusCode === 409
    );
}

function getList(
    statuses: ReservationStatus[],
    from?: string,
    to?: string,
    search?: string
): Promise<ReservationDTO[]> {
    const params = new URLSearchParams();
    if (from) {
        params.append('From', from);
    }
    if (to) {
        params.append('To', to);
    }
    statuses.forEach((status) => params.append('Statuses', status));
    if (search) {
        params.append('Search', search);
    }
    return agent.requests.get(`${BASE}/list`, { params });
}

function getDayBounds(day: string | null): [string | undefined, string | undefined] {
    return day
        ? [
              toWireDateTime(day, '00:00'),
              toWireDateTime(format(addDays(parseISO(day), 1), DATE_FORMAT), '00:00'),
          ]
        : [undefined, undefined];
}

function findTakenTableAfterConflict(
    model: UpdateReservationModel,
    excludeReservationId: string | null
): Promise<string | undefined> {
    return Reservations.getTablesAvailability(
        model.reservationStartDateTime,
        model.reservationDurationInMinutes,
        excludeReservationId
    )
        .catch((): TableAvailabilityDTO[] => [])
        .then((availability) => findTakenTable(model.tables, availability));
}

export const Reservations = {
    getById: (id: string): Promise<ReservationDTO> => agent.requests.get(`${BASE}/${id}`),

    changeStatus: (
        id: string,
        status: 'NoShow' | 'Completed',
        expectedVersion?: number
    ): Promise<ReservationDTO> =>
        agent.requests.patch(`${BASE}/${id}/status`, {
            status,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        }),

    cancel: (id: string, model: CancelReservationModel): Promise<CancelReservationResult> =>
        agent.requests
            .patch(`${BASE}/${id}/status`, { ...model, status: 'Cancelled' })
            .then(
                (result: {
                    reservation: ReservationDTO;
                    cancellationEmailFailed: boolean;
                }): CancelReservationResult => ({
                    status: 'cancelled',
                    ...result,
                })
            )
            .catch((error: unknown): CancelReservationResult | Promise<never> =>
                isConflict(error)
                    ? error.Code === 'reservationOrdersExist'
                        ? error.Tables?.length
                            ? { status: 'ordersExist', tables: error.Tables }
                            : Promise.reject(error)
                        : { status: 'alreadyHandled' }
                    : Promise.reject(error)
            ),

    getCancellationAvailability: (id: string): Promise<ReservationCancellationAvailabilityDTO> =>
        agent.requests.get(`${BASE}/${id}/cancellation-availability`),

    getAssignedTablesOrderState: (id: string): Promise<ReservationTableOrderStateDTO[]> =>
        agent.requests.get(`${BASE}/${id}/assigned-tables-order-state`),

    getRequests: (filter?: ReservationListFilter): Promise<ReservationDTO[]> =>
        getList(['Requested'], ...getDayBounds(filter?.day ?? null), filter?.search),

    getActiveReservations: (day: string | null, search?: string): Promise<ReservationDTO[]> =>
        getList(['Requested', 'Confirmed', 'Seated'], ...getDayBounds(day), search),

    getDaySummary: (day: string): Promise<ReservationDaySummaryDTO> =>
        agent.requests.get(`${BASE}/day-summary`, { params: { day } }),

    getTablesAvailability: (
        start: string,
        durationMinutes: number,
        excludeReservationId: string | null
    ): Promise<TableAvailabilityDTO[]> =>
        agent.requests.get(`${BASE}/tables-availability`, {
            params: { start, durationMinutes, excludeReservationId },
        }),

    saveRequest: (id: string, model: UpdateReservationModel): Promise<SaveRequestResult> =>
        agent.requests
            .put(`${BASE}/${id}`, model, undefined)
            .then(
                (result: ReservationResultDTO): SaveRequestResult => ({
                    status: 'saved',
                    reservation: result.reservation,
                })
            )
            .catch((error: unknown): Promise<SaveRequestResult> | SaveRequestResult =>
                isConflict(error) ? { status: 'alreadyHandled' } : Promise.reject(error)
            ),

    confirmRequest: (id: string, model: UpdateReservationModel): Promise<ConfirmRequestResult> =>
        agent.requests
            .post(`${BASE}/${id}/confirm`, model)
            .then(
                (result: ReservationResultDTO): ConfirmRequestResult => ({
                    status: 'confirmed',
                    reservation: result.reservation,
                })
            )
            .catch(async (error: unknown): Promise<ConfirmRequestResult> => {
                if (!isConflict(error)) {
                    throw error;
                }
                const tableId = await findTakenTableAfterConflict(model, id);
                return tableId ? { status: 'tableTaken', tableId } : { status: 'alreadyHandled' };
            }),

    updateConfirmed: (id: string, model: UpdateReservationModel): Promise<UpdateConfirmedResult> =>
        agent.requests
            .put(`${BASE}/${id}/confirmed`, model, prepareHeaders())
            .then(
                (result: ReservationResultDTO): UpdateConfirmedResult => ({
                    status: 'updated',
                    reservation: result.reservation,
                    updatedEmailSent: result.updatedEmailSent,
                    updatedEmailFailed: result.updatedEmailFailed,
                })
            )
            .catch(async (error: unknown): Promise<UpdateConfirmedResult> => {
                if (!isConflict(error)) throw error;
                if (error.Code === 'reservationChanged') return { status: 'alreadyHandled' };
                if (error.Code === 'tableTaken' && error.TableId) {
                    return { status: 'tableTaken', tableId: error.TableId };
                }
                const availability = await Reservations.getTablesAvailability(
                    model.reservationStartDateTime,
                    model.reservationDurationInMinutes,
                    id
                );
                const tableId = findTakenTable(model.tables, availability);
                return tableId ? { status: 'tableTaken', tableId } : { status: 'alreadyHandled' };
            }),

    updateSeated: (id: string, model: UpdateReservationModel): Promise<UpdateSeatedResult> =>
        agent.requests
            .put(`${BASE}/${id}/seated`, model, prepareHeaders())
            .then(
                (result: ReservationResultDTO): UpdateSeatedResult => ({
                    status: 'updated',
                    reservation: result.reservation,
                })
            )
            .catch((error: unknown): UpdateSeatedResult | Promise<never> => {
                if (!isConflict(error)) return Promise.reject(error);
                if (error.Code === 'reservationChanged') return { status: 'alreadyHandled' };
                if (error.TableId) {
                    if (error.Code === 'tableTaken') {
                        return { status: 'tableTaken', tableId: error.TableId };
                    }
                    if (error.Code === 'removedTableOrderOpen') {
                        return { status: 'removedTableOrderOpen', tableId: error.TableId };
                    }
                    if (error.Code === 'retainedTableTaken') {
                        return {
                            status: 'retainedTableTaken',
                            tableId: error.TableId,
                            message: error.Message,
                        };
                    }
                }
                return Promise.reject(error);
            }),

    declineRequest: (id: string, model: DeclineRequestModel): Promise<RequestClosureResult> =>
        agent.requests
            .patch(`${BASE}/${id}/decline`, model)
            .then(
                (reservation: ReservationDTO): RequestClosureResult => ({
                    status: 'declined',
                    reservation,
                })
            )
            .catch((error: unknown): Promise<never> | RequestClosureResult =>
                isConflict(error) ? { status: 'alreadyHandled' } : Promise.reject(error)
            ),

    cancelRequest: (id: string, model: CancelReservationModel): Promise<RequestClosureResult> =>
        agent.requests
            .post(`${BASE}/${id}/cancel-request`, { ...model, status: 'Cancelled' })
            .then(
                (result: {
                    reservation: ReservationDTO;
                    cancellationEmailFailed: boolean;
                }): RequestClosureResult => ({
                    status: 'cancelled',
                    ...result,
                })
            )
            .catch((error: unknown): Promise<never> | RequestClosureResult =>
                isConflict(error) ? { status: 'alreadyHandled' } : Promise.reject(error)
            ),

    archiveRequest: (id: string): Promise<ArchiveRequestResult> =>
        agent.requests
            .post(`${BASE}/${id}/archive`)
            .then((): ArchiveRequestResult => 'archived')
            .catch((error: unknown): Promise<ArchiveRequestResult> | ArchiveRequestResult =>
                isConflict(error) ? 'alreadyHandled' : Promise.reject(error)
            ),

    createReservation: (model: UpdateReservationModel): Promise<CreateReservationResult> =>
        agent.requests
            .post(`${BASE}/create`, model)
            .then(
                (result: ReservationResultDTO): CreateReservationResult => ({
                    status: 'created',
                    reservation: result.reservation,
                })
            )
            .catch(async (error: unknown): Promise<CreateReservationResult> => {
                if (!isConflict(error)) {
                    throw error;
                }
                const tableId = await findTakenTableAfterConflict(model, null);
                if (!tableId) {
                    throw error.Message;
                }
                return { status: 'tableTaken', tableId };
            }),
};
